import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { ROLES } from '@/constants';
import { leadershipService } from '@/features/leadership/services/leadershipService';
import { pillarChairService } from '@/features/pillarChair/services/pillarChairService';
import { fetchTaskForNotification } from '@/features/notifications/taskNotificationDetailApi';
import { useOverdueNotificationReply } from '@/features/notifications/useOverdueNotificationReply';

const REPLY_ROLES = new Set([
    ROLES.EXECUTIVE,
    ROLES.ADMIN,
    ROLES.SUPER_ADMIN,
    ROLES.TASK_FORCE_CHAIR,
    ROLES.PILLAR_CHAIR,
]);

async function assertTaskForceChairScope(expectedTaskForceId) {
    const body = await leadershipService.getMyTaskForce();
    const tf = body?.data ?? body;
    const myId = tf?.id;
    if (myId == null || String(myId) !== String(expectedTaskForceId)) {
        throw new Error('This task is not part of your task force.');
    }
}

async function assertPillarChairScope(expectedPillarId) {
    const body = await pillarChairService.getMyPillar();
    const pillar = body?.data ?? body;
    const myId = pillar?.id;
    if (myId == null || String(myId) !== String(expectedPillarId)) {
        throw new Error('This task is not part of your pillar.');
    }
}

export function TaskNotificationDetailModal({ notification, open, onClose, userId, userRole }) {
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [task, setTask] = useState(null);
    const { replyToOverdueTask, replyError, clearReplyError, setReplyError } = useOverdueNotificationReply();
    const [replyBusy, setReplyBusy] = useState(false);

    const canReply = useMemo(() => REPLY_ROLES.has(userRole), [userRole]);

    useEffect(() => {
        if (!open || !notification?.taskId) {
            setTask(null);
            setLoadError(null);
            clearReplyError();
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError(null);
            setTask(null);
            clearReplyError();
            try {
                const n = notification;
                let taskForceId = n.taskForceId;
                let pillarId = n.pillarId;
                const projectId = n.projectId;

                if (userRole === ROLES.TASK_FORCE_CHAIR && taskForceId) {
                    await assertTaskForceChairScope(taskForceId);
                }
                if (userRole === ROLES.PILLAR_CHAIR && pillarId) {
                    await assertPillarChairScope(pillarId);
                }

                if (!taskForceId || !pillarId) {
                    throw new Error(
                        'This notice does not include task force and pillar ids. The server should attach them to overdue notifications so the task can be loaded.',
                    );
                }

                const mapped = await fetchTaskForNotification(
                    {
                        taskId: String(n.taskId),
                        taskForceId: String(taskForceId),
                        pillarId: String(pillarId),
                        projectId: projectId || null,
                    },
                    userId,
                );
                if (cancelled) return;

                if (userRole === ROLES.PILLAR_CHAIR) {
                    const tid = mapped?.pillarId ?? mapped?._raw?.pillarId ?? mapped?._raw?.pillar?.id;
                    if (tid != null && String(tid) !== String(pillarId)) {
                        throw new Error('This task does not belong to your pillar.');
                    }
                }

                setTask(mapped);
            } catch (e) {
                if (!cancelled) setLoadError(e?.message || 'Could not load task.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, notification, userId, userRole, clearReplyError]);

    if (!open || !notification) return null;

    const n = notification;
    const pillarLabel =
        n.pillarName ||
        task?._raw?.pillar?.name ||
        (task?.pillarId != null ? `Pillar ${task.pillarId}` : '—');
    const taskForceLabel = n.taskForceName || task?._raw?.pillar?.taskForce?.name || '—';

    const displayName = task?.name || n.title || 'Task';
    const displayStart = task?.startDate || n.startDate || '—';
    const displayEnd = task?.endDate || n.endDate || '—';
    const displayStatus = task?.status || n.status || '—';

    const handleDismiss = () => {
        if (typeof n.onRead === 'function') n.onRead();
        onClose();
    };

    const handleReply = async () => {
        if (!task || !n.taskId) return;
        const raw = task._raw ?? {};
        const taskForceId = n.taskForceId || raw?.pillar?.taskForceId;
        const pillarId = n.pillarId || task.pillarId || raw?.pillarId || raw?.pillar?.id;
        if (!taskForceId || !pillarId) {
            setReplyError('Missing task force or pillar for chat routing.');
            return;
        }
        setReplyBusy(true);
        clearReplyError();
        try {
            const { ok } = await replyToOverdueTask({
                taskId: String(n.taskId),
                taskName: displayName,
                taskDetail: task,
                taskForceId: String(taskForceId),
                pillarId: String(pillarId),
                pillarName: pillarLabel,
                taskForceName: taskForceLabel !== '—' ? taskForceLabel : n.taskForceName,
            });
            if (ok) handleDismiss();
        } finally {
            setReplyBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-lg">
                <div className="flex items-center justify-between border-b border-border px-6 py-4">
                    <h3 className="text-lg font-semibold text-foreground">Task details</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        aria-label="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="space-y-4 px-6 py-5">
                    {loading ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                        </div>
                    ) : loadError ? (
                        <p className="text-sm text-destructive">{loadError}</p>
                    ) : (
                        <>
                            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Task
                                    </dt>
                                    <dd className="mt-0.5 font-medium text-foreground">{displayName}</dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Status
                                    </dt>
                                    <dd className="mt-0.5 text-foreground">{displayStatus}</dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Start
                                    </dt>
                                    <dd className="mt-0.5 text-foreground">{displayStart}</dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Due
                                    </dt>
                                    <dd className="mt-0.5 text-foreground">{displayEnd}</dd>
                                </div>
                                <div className="sm:col-span-2">
                                    <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Pillar
                                    </dt>
                                    <dd className="mt-0.5 text-foreground">{pillarLabel}</dd>
                                </div>
                                <div className="sm:col-span-2">
                                    <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Task force
                                    </dt>
                                    <dd className="mt-0.5 text-foreground">{taskForceLabel}</dd>
                                </div>
                            </dl>
                            {replyError ? <p className="text-sm text-destructive">{replyError}</p> : null}
                        </>
                    )}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border px-6 py-4">
                    <button type="button" onClick={handleDismiss} className="btn-secondary">
                        Dismiss
                    </button>
                    <button
                        type="button"
                        onClick={handleReply}
                        disabled={
                            replyBusy ||
                            !canReply ||
                            loading ||
                            Boolean(loadError) ||
                            !task
                        }
                        className="btn-primary"
                    >
                        {replyBusy ? 'Opening…' : 'Reply in chat'}
                    </button>
                </div>
            </div>
        </div>
    );
}
